import styled from 'styled-components';

import { DimeCloudApp } from '@tradeparadigm/terminal-react';

import {
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';
import { PARADIGM_CLOUD_TERMINAL_CONFIG } from '#/features/terminal/terminal-config';

import BlockTradeContextPump from '#/components/Terminal/BlockTradeContextPump';
import PendingMessageDrain from '#/components/Terminal/PendingMessageDrain';
import TerminalThemeScope from '#/components/Terminal/TerminalThemeScope';

/**
 * The shared Dime Terminal (`@tradeparadigm/terminal-react`) inside
 * Paradigm's drawer chrome. The package owns everything inside — sessions
 * sidebar, chat, skills, tasks, sign-in and resize handles — while this file
 * owns only the host strip above it (title + pin-mode toggle).
 *
 * Sign-in and connection are the DIME cloud flow's concern (`DimeCloudApp`):
 * Clerk sign-in, access-code onboarding, and the server-resolved gateway
 * grant all ship in the package. Requires `<DimeTerminalProvider>` above
 * this component (mounted once in App.tsx).
 */
export default function BlockTerminalTab() {
  const drawerState = useTerminalDrawerState();

  return (
    <Container>
      <Header />
      <TerminalThemeScope>
        <DimeCloudApp
          config={PARADIGM_CLOUD_TERMINAL_CONFIG}
          // The host strip above already frames the terminal; the packaged
          // signed-in email + Log Out bar would duplicate it. Sign-out stays
          // reachable via the terminal sidebar's account menu.
          hideAuthShell
          // Bottom-tab is vertically cramped (Paradigm's own header already
          // sits above the terminal); compact mode drops the package's top
          // bar so chat gets that row back. The roomy side panel keeps it.
          compact={drawerState.pinMode === 'bottom-tab'}
          agentEffects={
            <>
              <BlockTradeContextPump />
              <PendingMessageDrain />
            </>
          }
        />
      </TerminalThemeScope>
    </Container>
  );
}

function Header() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();
  return (
    <HeaderRow>
      <Title>DIME Terminal</Title>
      <Spacer />
      <PinSegment role="group" aria-label="Terminal pin mode">
        <PinSegmentButton
          $active={drawerState.pinMode === 'bottom-tab'}
          onClick={() => drawerActions.setPinMode('bottom-tab')}
          type="button"
          title="Pin to bottom"
          aria-label="Pin to bottom"
          aria-pressed={drawerState.pinMode === 'bottom-tab'}
        >
          <BottomDockIcon />
        </PinSegmentButton>
        <PinSegmentButton
          $active={drawerState.pinMode === 'side-panel'}
          onClick={() => {
            drawerActions.setPinMode('side-panel');
            // Ensure drawer stays open when switching to side-panel
            if (!drawerState.isOpen) {
              drawerActions.open();
            }
          }}
          type="button"
          title="Pin to side"
          aria-label="Pin to side"
          aria-pressed={drawerState.pinMode === 'side-panel'}
        >
          <SideDockIcon />
        </PinSegmentButton>
      </PinSegment>
    </HeaderRow>
  );
}

const Container = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

const Title = styled.h2`
  margin: 0;
  padding: 0 4px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1;
  color: #fff;
  letter-spacing: 0.02em;
`;

const Spacer = styled.div`
  flex: 1;
`;

const PinSegment = styled.div`
  display: flex;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  overflow: hidden;
`;

const PinSegmentButton = styled.button<{ $active: boolean }>`
  /* Icon-only — size the hit area generously around the 14px glyph so
     the segmented control still has Bottom/Side's old visual footprint. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 22px;
  padding: 0;
  background: ${(p) =>
    p.$active ? 'var(--hover-bg, rgba(255, 255, 255, 0.08))' : 'transparent'};
  color: ${(p) =>
    p.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  & + & {
    border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  }

  &:hover {
    color: var(--text-primary);
  }
`;

/**
 * Small mockup of the app window with the terminal docked at the bottom
 * (filled strip across the lower third). Inherits color so the active /
 * inactive states from PinSegmentButton flow through unchanged.
 */
function BottomDockIcon() {
  return (
    <DockIconSvg viewBox="0 0 14 14" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="12"
        height="12"
        rx="1.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeOpacity="0.7"
      />
      <rect
        x="2.25"
        y="8.25"
        width="9.5"
        height="3.5"
        rx="0.6"
        fill="currentColor"
      />
    </DockIconSvg>
  );
}

/**
 * Same window mockup with the terminal docked on the right edge.
 */
function SideDockIcon() {
  return (
    <DockIconSvg viewBox="0 0 14 14" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="12"
        height="12"
        rx="1.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeOpacity="0.7"
      />
      <rect
        x="8.25"
        y="2.25"
        width="3.5"
        height="9.5"
        rx="0.6"
        fill="currentColor"
      />
    </DockIconSvg>
  );
}

const DockIconSvg = styled.svg`
  width: 14px;
  height: 14px;
  display: block;
  flex-shrink: 0;
`;

// Re-export drawer types so other components can flag-gate without an
// extra import.
export type { PinMode } from '#/features/terminal/drawer-context';
